import { apiService } from './api';

export interface TemplateOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
}

export interface StyleOption {
  id: string;
  name: string;
  description: string;
  preview: string;
}

export interface InteractivityOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  integration: string;
}

export interface TemplateConfig {
  templates: TemplateOption[];
  styles: StyleOption[];
  interactivity: InteractivityOption[];
}

class TemplateConfigService {
  private config: TemplateConfig | null = null;
  private fetchPromise: Promise<TemplateConfig> | null = null;

  /**
   * Fetch template configuration from server
   * Uses caching to avoid multiple requests
   */
  async getConfig(): Promise<TemplateConfig> {
    // Return cached config if available
    if (this.config) {
      return this.config;
    }

    // Return existing fetch promise if in progress
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Start new fetch
    this.fetchPromise = this.fetchConfigFromServer();
    
    try {
      this.config = await this.fetchPromise;
      return this.config;
    } finally {
      this.fetchPromise = null;
    }
  }

  private async fetchConfigFromServer(): Promise<TemplateConfig> {
    try {
      console.log('📥 Fetching template configuration from server...');
      const response = await apiService.get('/editor/templates/config');
      
      if (response.data.success && response.data.data) {
        console.log('✅ Template configuration loaded:', {
          templates: response.data.data.templates.length,
          styles: response.data.data.styles.length,
          interactivity: response.data.data.interactivity.length
        });
        return response.data.data;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('❌ Failed to fetch template configuration:', error);
      
      // Return fallback configuration
      return this.getFallbackConfig();
    }
  }

  /**
   * Fallback configuration in case server is unreachable
   * Minimal set to ensure app doesn't break
   */
  private getFallbackConfig(): TemplateConfig {
    console.warn('⚠️ Using fallback template configuration');
    return {
      templates: [
        {
          id: 'blank',
          name: 'Blank Canvas',
          description: 'Start from scratch',
          icon: '🎮',
          category: 'starter'
        }
      ],
      styles: [
        {
          id: 'beemi',
          name: 'Beemi',
          description: 'Vibrant and playful',
          preview: 'vibrant, gradients'
        }
      ],
      interactivity: [
        {
          id: 'likes',
          name: 'Likes',
          description: 'Use likes for voting',
          icon: '👍',
          integration: 'likes'
        },
        {
          id: 'comments',
          name: 'Comments',
          description: 'Use comments for input',
          icon: '💬',
          integration: 'comments'
        }
      ]
    };
  }

  /**
   * Clear cached configuration (useful for testing)
   */
  clearCache(): void {
    this.config = null;
    this.fetchPromise = null;
  }
}

// Export singleton instance
export const templateConfigService = new TemplateConfigService();

